import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { CheckCircle, XCircle, X, Save, FileCheck, AlertTriangle, Edit2 } from 'lucide-react';
import config from '../config/config';
import '../styles/DataChecklistModal.css';

const DataChecklistModal = ({ isOpen, onClose, documentType, noTiket, onStatusUpdate }) => {
  const [checklist, setChecklist] = useState([]);
  const [checklistItems, setChecklistItems] = useState({});
  const [userInputs, setUserInputs] = useState({}); // Store user input from 12 Elemen SPD
  const [editMode, setEditMode] = useState({}); // Track which items are in edit mode
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && documentType) {
      fetchChecklist();
      fetchExistingChecklistData();
    }
  }, [isOpen, documentType, noTiket]);

  const fetchChecklist = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${config.API_URL}/api/checklist/${documentType}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        setChecklist(response.data.checklist);
      }
    } catch (error) {
      console.error('Error fetching checklist:', error);
      toast.error('Gagal memuat checklist');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingChecklistData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${config.API_URL}/api/checklist/data/${noTiket}/${documentType}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success && response.data.checklistData) {
        const existingItems = response.data.checklistData.items || {};
        
        // Convert string keys to proper format and ensure boolean values
        const convertedItems = {};
        const convertedInputs = {};
        Object.keys(existingItems).forEach(key => {
          const value = existingItems[key];
          
          // Handle new format with userInput
          if (typeof value === 'object' && value !== null) {
            convertedItems[key] = value.checked || false;
            convertedInputs[key] = value.userInput || '';
          } else {
            // Handle old format (boolean only)
            convertedItems[key] = value === true || value === 'true' || value === 1;
          }
        });
        
        console.log('Loaded existing checklist items:', convertedItems);
        console.log('Loaded user inputs:', convertedInputs);
        setChecklistItems(convertedItems);
        setUserInputs(convertedInputs);
      } else {
        // No existing data, start with empty state
        setChecklistItems({});
        setUserInputs({});
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        // No existing checklist data, start fresh
        setChecklistItems({});
      } else {
        console.error('Error fetching existing checklist data:', error);
      }
    }
  };

  const handleChecklistChange = (itemId, checked) => {
    setChecklistItems(prev => ({
      ...prev,
      [itemId]: checked
    }));
  };

  const handleUserInputChange = (itemId, value) => {
    setUserInputs(prev => ({
      ...prev,
      [itemId]: value
    }));
  };

  const toggleEditMode = (itemId) => {
    setEditMode(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const getProgressInfo = () => {
    const mandatoryItems = checklist.filter(item => item.priority === 'mandatory');
    const optionalItems = checklist.filter(item => item.priority === 'optional');
    
    const checkedMandatory = mandatoryItems.filter(item => checklistItems[item.id]).length;
    const checkedOptional = optionalItems.filter(item => checklistItems[item.id]).length;
    
    const totalMandatory = mandatoryItems.length;
    const totalOptional = optionalItems.length;
    
    const checkedCount = checkedMandatory + checkedOptional;
    const totalCount = totalMandatory + totalOptional;
    const percentage = totalCount > 0 ? Math.round((checkedCount / totalCount) * 100) : 0;
    
    // Status based on mandatory items only
    const status = checkedMandatory === totalMandatory ? 'Sesuai' : 'Tidak Sesuai';
    
    return {
      checkedCount,
      totalCount,
      checkedMandatory,
      totalMandatory,
      checkedOptional,
      totalOptional,
      percentage,
      status
    };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const { status } = getProgressInfo();
      
      // Save checkbox states to checklist_data table
      const checkboxPayload = {
        documentType,
        items: checklistItems
      };

      await axios.post(
        `${config.API_URL}/api/checklist/data/${noTiket}`,
        checkboxPayload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Save user input text to data.checklist_data (JSONB column)
      if (Object.keys(userInputs).length > 0) {
        await axios.put(
          `${config.API_URL}/api/checklist/user-input/${noTiket}`,
          { checklistData: userInputs },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }

      // Refresh data from DB to ensure sync
      await fetchExistingChecklistData();

      toast.success('Checklist berhasil disimpan!');
      
      // Notify parent component about status update
      if (onStatusUpdate) {
        await onStatusUpdate(documentType, status);
      }
      
      // Don't close modal immediately, let user see the updated state
      // onClose();
    } catch (error) {
      console.error('Error saving checklist:', error);
      toast.error('Gagal menyimpan checklist');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const progressInfo = getProgressInfo();

  return (
    <div className="checklist-modal-overlay">
      <div className="checklist-modal">
        <div className="checklist-modal-header">
          <div className="checklist-header-content">
            <FileCheck size={24} className="checklist-icon" />
            <div>
              <h3>Checklist Pengecekan Data</h3>
              <p>Dokumen: <strong>{documentType}</strong></p>
            </div>
          </div>
          <button onClick={onClose} className="checklist-close-btn" disabled={saving}>
            <X size={20} />
          </button>
        </div>

        <div className="checklist-modal-body">
          {loading ? (
            <div className="checklist-loading">
              <div className="loading-spinner"></div>
              <p>Memuat checklist...</p>
            </div>
          ) : (
            <>
              <div className="checklist-status-summary">
                <div className="status-info">
                  <div className="status-badge">
                    <span className={`status-indicator ${progressInfo.status === 'Sesuai' ? 'status-sesuai' : 'status-tidak-sesuai'}`}>
                      {progressInfo.status === 'Sesuai' ? (
                        <CheckCircle size={16} />
                      ) : (
                        <XCircle size={16} />
                      )}
                      {progressInfo.status}
                    </span>
                  </div>
                  <div className="status-count">
                    <div className="count-detail">
                      <span className="mandatory-count">
                        Wajib: {progressInfo.checkedMandatory}/{progressInfo.totalMandatory}
                      </span>
                      <span className="optional-count">
                        Opsional: {progressInfo.checkedOptional}/{progressInfo.totalOptional}
                      </span>
                    </div>
                    <div className="total-count">
                      Total: {progressInfo.checkedCount} dari {progressInfo.totalCount} item
                    </div>
                  </div>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${progressInfo.percentage}%` }}
                  ></div>
                </div>
              </div>

              <div className="checklist-items">
                {checklist.length === 0 ? (
                  <div className="no-checklist">
                    <AlertTriangle size={24} />
                    <p>Belum ada checklist untuk dokumen ini</p>
                  </div>
                ) : (
                  <>
                    {/* Mandatory Items Section */}
                    {checklist.filter(item => item.priority === 'mandatory').length > 0 && (
                      <>
                        <div className="checklist-section-header mandatory">
                          <span className="section-title">Item Wajib</span>
                          <span className="section-count">
                            {checklist.filter(item => item.priority === 'mandatory').length} item
                          </span>
                        </div>
                        {checklist
                          .filter(item => item.priority === 'mandatory')
                          .map((item) => (
                            <div key={item.id} className="checklist-item">
                              <label className="checklist-label">
                                <input
                                  type="checkbox"
                                  checked={checklistItems[item.id] || false}
                                  onChange={(e) => handleChecklistChange(item.id, e.target.checked)}
                                  className="checklist-checkbox"
                                />
                                <span className="checkmark"></span>
                                <div className="checklist-content">
                                  <div className="checklist-text-with-priority">
                                    <span className="checklist-text">{item.description}</span>
                                    <span className="priority-badge priority-mandatory">Wajib</span>
                                  </div>
                                  {/* Always show input container to add notes */}
                                  <div className="user-input-container">
                                    <div className="input-header">
                                      <label className="input-label">Catatan :</label>
                                      <button
                                        type="button"
                                        className="edit-toggle-btn"
                                        onClick={() => toggleEditMode(item.id)}
                                        title={editMode[item.id] ? 'Selesai Edit' : 'Edit Catatan'}
                                      >
                                        <Edit2 size={14} />
                                        {editMode[item.id] ? 'Selesai' : 'Edit'}
                                      </button>
                                    </div>
                                    {editMode[item.id] ? (
                                      <textarea
                                        className="input-textarea"
                                        placeholder={item.input_hint || 'Masukkan catatan verifikasi...'}
                                        value={userInputs[item.id] || ''}
                                        onChange={(e) => handleUserInputChange(item.id, e.target.value)}
                                        rows={3}
                                      />
                                    ) : (
                                      <div className="input-display-box">
                                        {userInputs[item.id] || <span className="empty-text">Belum ada catatan</span>}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </label>
                            </div>
                          ))}
                      </>
                    )}

                    {/* Optional Items Section */}
                    {checklist.filter(item => item.priority === 'optional').length > 0 && (
                      <>
                        <div className="checklist-section-header optional">
                          <span className="section-title">Item Opsional</span>
                          <span className="section-count">
                            {checklist.filter(item => item.priority === 'optional').length} item
                          </span>
                        </div>
                        {checklist
                          .filter(item => item.priority === 'optional')
                          .map((item) => (
                            <div key={item.id} className="checklist-item">
                              <label className="checklist-label">
                                <input
                                  type="checkbox"
                                  checked={checklistItems[item.id] || false}
                                  onChange={(e) => handleChecklistChange(item.id, e.target.checked)}
                                  className="checklist-checkbox"
                                />
                                <span className="checkmark"></span>
                                <div className="checklist-content">
                                  <div className="checklist-text-with-priority">
                                    <span className="checklist-text">{item.description}</span>
                                    <span className="priority-badge priority-optional">Opsional</span>
                                  </div>
                                  {/* Always show input container to add notes */}
                                  <div className="user-input-container">
                                    <div className="input-header">
                                      <label className="input-label">Catatan :</label>
                                      <button
                                        type="button"
                                        className="edit-toggle-btn"
                                        onClick={() => toggleEditMode(item.id)}
                                        title={editMode[item.id] ? 'Selesai Edit' : 'Edit Catatan'}
                                      >
                                        <Edit2 size={14} />
                                        {editMode[item.id] ? 'Selesai' : 'Edit'}
                                      </button>
                                    </div>
                                    {editMode[item.id] ? (
                                      <textarea
                                        className="input-textarea"
                                        placeholder={item.input_hint || 'Masukkan catatan verifikasi...'}
                                        value={userInputs[item.id] || ''}
                                        onChange={(e) => handleUserInputChange(item.id, e.target.value)}
                                        rows={3}
                                      />
                                    ) : (
                                      <div className="input-display-box">
                                        {userInputs[item.id] || <span className="empty-text">Belum ada catatan</span>}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </label>
                            </div>
                          ))}
                      </>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <div className="checklist-modal-footer">
          <button 
            onClick={onClose} 
            className="btn-cancel"
            disabled={saving}
          >
            Batal
          </button>
          <button 
            onClick={handleSave} 
            className="btn-save"
            disabled={saving || loading}
          >
            {saving ? (
              <>
                <div className="btn-spinner"></div>
                Menyimpan...
              </>
            ) : (
              <>
                <Save size={16} />
                Simpan Checklist
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataChecklistModal;
